import React, { useEffect, useState } from "react";
import { styled } from "../stitches.config";
import Footer from "../redesign/components/Footer";
import Link from "../components/Link";
import PageCover from "../redesign/components/PageCover";
import { ReactComponent as ChevronIcon } from "../redesign/assets/icons/chevron-menu-mobile.svg?react";
import Breadcrumbs from "../redesign/components/Breadcrumbs";

const domain = window.APP_SETTINGS.domain;
const API_URL =
  `https://${domain}/api/posts-day`;

type ArchiveData = {
  [year: string]: {
    [month: string]: string[];
  };
};

const Chevron = () => (
  <span aria-hidden="true" style={{ marginLeft: 8 }}>
    <ChevronIcon className="chevronIcon" />
  </span>
);

const ArchivePage: React.FC = () => {
  const [archiveData, setArchiveData] = useState<ArchiveData>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArchive = async () => {
      try {
        const response = await fetch(API_URL);
        const data = await response.json();

        setArchiveData(data);
      } catch (error) {
        console.error("Błąd pobierania newsroomu:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArchive();
  }, []);

  if (loading) {
    return <div>Ładowanie...</div>;
  }

  const years = Object.keys(archiveData).sort(
    (a, b) => Number(b) - Number(a)
  );

  return (
    <>
      <Wrapper>
        <PageCover
          beforeContent={
            <Breadcrumbs
              category={{ name: "Newsroom", link: "/newsroom" }}
              allWhite
            />
          }
          title="Newsroom"
          description=""
          isDescriptionWyswig={false}
        />

        <div className="container">
          <ArticleList className="row col-lg-12">
            {years.map((year, index) => {
              const isOpen = index < 3;
              const months = archiveData[year];

              return (
                <YearBlock key={year}>
                  <Details open={isOpen}>
                    <Summary>
                      <YearHeading>
                        {year} <Chevron />
                      </YearHeading>
                    </Summary>

                    <MonthsWrapper>
                      {Object.entries(months).map(([monthName, days]) => (
                        <MonthBlock key={monthName}>
                          <MonthHeading>{monthName}</MonthHeading>

                          <DaysColumn>
                            {days.map((dateStr) => (
                              <DayItem key={dateStr}>
                                <Link to={`/newsroom/${dateStr}`}>
                                  {dateStr}
                                </Link>
                              </DayItem>
                            ))}
                          </DaysColumn>
                        </MonthBlock>
                      ))}
                    </MonthsWrapper>
                  </Details>
                </YearBlock>
              );
            })}
          </ArticleList>
        </div>
      </Wrapper>

      <Footer />
    </>
  );
};

const Wrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  color: "$grayscale100",

  "& .container": {
    marginRight: "0px",
    marginLeft: "0px",

    "@bp3": {
      marginRight: "auto",
      marginLeft: "auto",
    },
  },
});

const ArticleList = styled("div", {
  marginTop: "40px",
});

const YearBlock = styled("div", {});

const Details = styled("details", {
  borderBottom: "1px solid #eee",
});

const Summary = styled("summary", {
  cursor: "pointer",
  listStyle: "none",

  "&::-webkit-details-marker": {
    display: "none",
  },
});

const YearHeading = styled("h2", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",

  span: {
    transform: "rotate(90deg)",

    svg: {
      width: "20px",
      height: "20px",
    },
  },

  "details[open] & span": {
    transform: "rotate(270deg)",
  },
});

const MonthsWrapper = styled("div", {
  margin: "16px 0px",
});

const MonthBlock = styled("div", {
  marginBottom: "24px",
});

const MonthHeading = styled("h3", {
  fontSize: "16px",
  marginBottom: "8px",
});

const DaysColumn = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
});

const DayItem = styled("div", {
  padding: "4px 0px",
  width: "fit-content",

  a: {
    color: "$grayscale100",
  },
});

export default ArchivePage;